import { useEffect, useMemo } from 'react'
import { useClientStore } from '../../stores/clientStore'
import { useBuildingStore } from '../../stores/buildingStore'
import { useAuthStore } from '../../stores/authStore'
import { usePermissionStore } from '../../stores/permissionStore'
import { useCanAccess } from '../../hooks/useCanAccess'
import type { Client } from '../../types/client'

function buildHierarchy(clients: Client[]): Client[] {
  const map = new Map<string, Client>()
  const roots: Client[] = []
  for (const c of clients) {
    map.set(c.id, { ...c, children: [] })
  }
  for (const c of map.values()) {
    if (c.parent_id && map.has(c.parent_id)) {
      map.get(c.parent_id)!.children!.push(c)
    } else {
      roots.push(c)
    }
  }
  return roots
}

function flattenHierarchy(clients: Client[], depth = 0): { client: Client; depth: number }[] {
  const result: { client: Client; depth: number }[] = []
  for (const c of clients) {
    result.push({ client: c, depth })
    if (c.children?.length) {
      result.push(...flattenHierarchy(c.children, depth + 1))
    }
  }
  return result
}

export default function ClientSelector() {
  const clients = useClientStore((s) => s.clients)
  const currentClientId = useClientStore((s) => s.currentClientId)
  const setCurrentClient = useClientStore((s) => s.setCurrentClient)
  const fetchClients = useClientStore((s) => s.fetchClients)
  const loading = useClientStore((s) => s.loading)
  const token = useAuthStore((s) => s.token)
  const canReadClients = useCanAccess('clients', 'read')
  const permissionsLoaded = usePermissionStore((s) => s.loaded)

  const resetSite = useBuildingStore((s) => s.resetSite)
  const fetchSites = useBuildingStore((s) => s.fetchSites)

  // Compute hierarchy with useMemo instead of Zustand selector (avoids new refs on every store change)
  const hierarchy = useMemo(() => buildHierarchy(clients), [clients])

  const isSuperadmin = usePermissionStore((s) => s.isSuperadmin)
  const clientError = useClientStore((s) => s.error)

  // Fetch clients once permissions are loaded and user has access
  useEffect(() => {
    if (token && permissionsLoaded && canReadClients && clients.length === 0) {
      fetchClients()
    }
  }, [token, permissionsLoaded, canReadClients, isSuperadmin, clients.length, fetchClients])

  // Auto-select if single client
  useEffect(() => {
    if (clients.length === 1 && !currentClientId && clients[0]) {
      setCurrentClient(clients[0].id)
    }
  }, [clients.length, currentClientId, setCurrentClient, clients])

  // Reload sites when client changes (pass clientId to filter)
  useEffect(() => {
    if (currentClientId) {
      resetSite()
      fetchSites(currentClientId)
    }
  }, [currentClientId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Hide if no permission
  if (!canReadClients) return null
  // Hide for non-superadmin with single client (superadmin always sees the selector)
  if (!isSuperadmin && clients.length <= 1) return null
  if (loading) return <span className="app-header__site">Clients...</span>
  // Show error if fetch failed
  if (clientError && clients.length === 0) return <span className="app-header__site" title={clientError}>Erreur clients</span>

  const flat = flattenHierarchy(hierarchy)

  return (
    <select
      className="site-selector"
      value={currentClientId ?? ''}
      onChange={(e) => setCurrentClient(e.target.value || null)}
    >
      <option value="">-- Client --</option>
      {flat.map(({ client, depth }) => (
        <option key={client.id} value={client.id}>
          {'\u00A0'.repeat(depth * 3)}{client.name}
        </option>
      ))}
    </select>
  )
}
